if (!customElements.get('featured-collection')) {
  customElements.define(
    'featured-collection',
    class FeaturedCollection extends HTMLElement {
      constructor() {
        super();
        this.sliderInstance = false;
        this.onViewportChange = this.init.bind(this);
        this.onBlockSelect = this.handleBlockSelect.bind(this);
      }

      connectedCallback() {
        this.selectors = {
          sliderWrapper: '.featured-collection__items',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        };

        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`);
        this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

        this.enableSlider = this.dataset.enableSlider === 'true';
        this.items = parseInt(this.dataset.items, 10) || 4;
        this.tabletItems = parseInt(this.dataset.tabletItems, 10) || 2;
        this.mobileItems = parseInt(this.dataset.mobileItems, 10) || 1;
        this.autoplayDelay = parseInt(this.dataset.autoplayDelay, 10) || 0;
        this.autoplay =
          this.dataset.autoplay === 'true' && this.autoplayDelay > 0;

        if (!this.enableSlider || !this.sliderWrapper) return;

        this.mediaQuery = window.matchMedia(
          FoxTheme.config.mediaQueryMobile
        );

        this.init();

        if (this.mediaQuery.addEventListener) {
          this.mediaQuery.addEventListener(
            'change',
            this.onViewportChange
          );
        } else {
          this.mediaQuery.addListener(this.onViewportChange);
        }

        if (Shopify.designMode) {
          document.addEventListener(
            'shopify:block:select',
            this.onBlockSelect
          );
        }
      }

      init() {
        // Keep the carousel active on mobile when autoplay is enabled.
        // When autoplay is off, preserve the theme's mobile grid layout.
        if (FoxTheme.config.mqlMobile && !this.autoplay) {
          this.destroySlider();
        } else {
          this.initSlider();
        }
      }

      initSlider() {
        if (this.sliderInstance?.slider) return;

        const gapValue = window
          .getComputedStyle(this.sliderWrapper)
          .getPropertyValue('--column-gap')
          .trim();

        const gapNumber = parseFloat(gapValue) || 0;
        const configuredGap = gapValue.includes('px')
          ? gapNumber
          : gapNumber *
            (parseFloat(
              getComputedStyle(document.documentElement).fontSize
            ) || 10);

        // Tighten the gap between product slides.
        const spaceBetween = Math.round(configuredGap * 0.6);

        this.sliderOptions = {
          slidesPerView: this.mobileItems,
          spaceBetween: spaceBetween,
          grabCursor: true,
          allowTouchMove: true,
          navigation: {
            nextEl: this.section?.querySelector(this.selectors.nextEl),
            prevEl: this.section?.querySelector(this.selectors.prevEl),
          },
          pagination: false,
          breakpoints: {
            768: {
              slidesPerView:
                this.tabletItems > 3 ? 3 : this.tabletItems,
            },
            1280: {
              slidesPerView: this.items,
            },
          },
          loop: true,
          threshold: 2,
        };

        if (this.autoplay) {
          this.sliderOptions.autoplay = {
            delay: this.autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          };
        }

        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(
          this,
          this.sliderOptions,
          this.autoplay ? [FoxTheme.Swiper.Autoplay] : undefined
        );

        this.sliderInstance.init();

        this.calcNavButtonsPosition();
        this.calcHeadingColumnHeight();
        this.fixQuickviewDuplicate();

        const focusableElements =
          FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            if (!this.sliderInstance?.slider) return;

            const slide = element.closest('.swiper-slide');
            const index = Array.from(
              this.sliderInstance.slider.slides
            ).indexOf(slide);

            if (index >= 0) {
              this.sliderInstance.slider.slideTo(index);
            }
          });
        });
      }

      destroySlider() {
        if (!this.sliderWrapper) return;

        this.classList.remove(this.classes.swiper);
        this.sliderWrapper.classList.remove(
          this.classes.swiperWrapper
        );
        this.sliderWrapper.classList.add(this.classes.grid);

        if (this.sliderInstance?.slider) {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = false;
        }
      }

      calcNavButtonsPosition() {
        if (this.dataset.calcButtonPosition !== 'true') return;

        const firstMedia = this.querySelector(
          '.product-card__image-wrapper'
        );

        if (firstMedia && firstMedia.clientHeight > 0) {
          this.section.style.setProperty(
            '--swiper-navigation-top-offset',
            `${parseInt(firstMedia.clientHeight, 10) / 2}px`
          );
        }
      }

      calcHeadingColumnHeight() {
        if (this.dataset.calcHeadingColumnHeight !== 'true') return;

        const firstMedia = this.querySelector(
          '.product-card__image-wrapper'
        );

        if (firstMedia && firstMedia.clientHeight > 0) {
          this.section.style.setProperty(
            '--heading-column-height',
            `${firstMedia.clientHeight}px`
          );
        }
      }

      fixQuickviewDuplicate() {
        const modalIds = [];

        this.querySelectorAll('quick-view-modal').forEach((modal) => {
          const modalID = modal.getAttribute('id');

          if (modalIds.includes(modalID)) {
            modal.remove();
          } else {
            modalIds.push(modalID);
          }
        });
      }

      handleBlockSelect(event) {
        if (
          event.detail.sectionId !== this.sectionId ||
          !this.sliderInstance?.slider
        ) {
          return;
        }

        const index = Number(event.target.dataset.index);
        if (Number.isNaN(index)) return;

        if (
          this.autoplay &&
          this.sliderInstance.slider.autoplay
        ) {
          this.sliderInstance.slider.autoplay.stop();
        }

        this.sliderInstance.slider.slideToLoop(index);
      }

      disconnectedCallback() {
        if (this.mediaQuery) {
          if (this.mediaQuery.removeEventListener) {
            this.mediaQuery.removeEventListener(
              'change',
              this.onViewportChange
            );
          } else {
            this.mediaQuery.removeListener(
              this.onViewportChange
            );
          }
        }

        document.removeEventListener(
          'shopify:block:select',
          this.onBlockSelect
        );

        if (this.sliderInstance?.slider) {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = false;
        }
      }
    }
  );
}